import {Injectable} from '@angular/core';
import {BehaviorSubject, finalize, forkJoin, Observable, switchMap} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {DataManipulationService} from 'src/app/services/data-manipulation.service';
import {
  PyramidCategory,
  PyramidLegendItem
} from 'src/app/components/categories-pyramid-display/categories-pyramid-display.component';
import {TeamDynamicService} from '../services/team-dynamic.service';
import {TeamDynamicRequestDto} from '../dto/team-dynamic-request.dto';
import {OverallScoreVm, ScoreDevelopmentChipVm, ScoreDevelopmentVm, TeamOverviewVm} from '../models/team-dynamic.vm';
import {
  mapOverallScore,
  mapPyramidCategories,
  mapScoreDevelopment,
  mapScoreDevelopmentChips,
  mapTeamOverview
} from '../mappers/team-dynamic.mapper';

@Injectable({
  providedIn: 'root'
})
export class TeamDynamicFacade {
  public intervals: any[] = [];

  private loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();
  private overallScoreSubject = new BehaviorSubject<OverallScoreVm | null>(null);
  readonly overallScore$ = this.overallScoreSubject.asObservable();
  private pyramidCategoriesSubject = new BehaviorSubject<PyramidCategory[]>([]);
  readonly pyramidCategories$ = this.pyramidCategoriesSubject.asObservable();
  private pyramidLegendSubject = new BehaviorSubject<PyramidLegendItem[]>([]);
  readonly pyramidLegend$ = this.pyramidLegendSubject.asObservable();
  private scoreDevelopmentSubject = new BehaviorSubject<ScoreDevelopmentVm>({labels: [], values: []});
  readonly scoreDevelopment$ = this.scoreDevelopmentSubject.asObservable();
  private loadingScoreDevelopmentSubject = new BehaviorSubject<boolean>(false);
  readonly loadingScoreDevelopment$ = this.loadingScoreDevelopmentSubject.asObservable();
  private scoreDevelopmentChipsSubject = new BehaviorSubject<ScoreDevelopmentChipVm[]>([]);
  readonly scoreDevelopmentChips$ = this.scoreDevelopmentChipsSubject.asObservable();
  private teamOverviewSubject = new BehaviorSubject<TeamOverviewVm | null>(null);
  readonly teamOverview$ = this.teamOverviewSubject.asObservable();
  private loadingTeamOverviewSubject = new BehaviorSubject<boolean>(false);
  readonly loadingTeamOverview$ = this.loadingTeamOverviewSubject.asObservable();

  constructor(private teamDynamicApi: TeamDynamicService, private data: DataManipulationService,
              private translate: TranslateService) {
  }

  loadData(payload: TeamDynamicRequestDto): void {
    this.loadingSubject.next(true);
    this.setPyramidLegend();

    this.teamDynamicApi.getIndicatorInterval().pipe(
      switchMap((intervals: any[]) => {
        this.intervals = [...(intervals ?? [])];
        return this.loadReports(payload);
      }),
      finalize(() => this.loadingSubject.next(false))
    ).subscribe((res: any) => {
      this.pushReports(res.workspace, res.teams, res.scores);
    });
  }

  reloadForPeriod(payload: TeamDynamicRequestDto): void {
    this.loadingTeamOverviewSubject.next(true);
    this.loadingScoreDevelopmentSubject.next(true);

    this.loadReports(payload).pipe(
      finalize(() => {
        this.loadingTeamOverviewSubject.next(false);
        this.loadingScoreDevelopmentSubject.next(false);
      })
    ).subscribe((res: any) => {
      this.pushReports(res.workspace, res.teams, res.scores);
    });
  }

  loadScoreDevelopment(payload: TeamDynamicRequestDto): void {
    this.loadingScoreDevelopmentSubject.next(true);

    this.teamDynamicApi.getTeamDynamicDevelopmentScores(this.getScoreDevelopmentOptions(payload)).pipe(
      finalize(() => this.loadingScoreDevelopmentSubject.next(false))
    ).subscribe((res: any) => {
      this.scoreDevelopmentSubject.next(mapScoreDevelopment(res));
    });
  }

  private loadReports(payload: TeamDynamicRequestDto): Observable<any> {
    return forkJoin({
      workspace: this.teamDynamicApi.getWorkspaceTeamDynamic(payload.startDate, payload.endDate),
      teams: this.teamDynamicApi.getTeamsTeamDynamic(payload.startDate, payload.endDate),
      scores: this.teamDynamicApi.getTeamDynamicDevelopmentScores(this.getScoreDevelopmentOptions(payload))
    });
  }

  private pushReports(workspace: any, teams: any, scores: any): void {
    const workspaceReport = workspace && !workspace.error ? workspace : null;
    const teamsReport = teams && !teams.error ? teams : null;
    const categories = mapPyramidCategories(workspaceReport, this.intervals, this.data.colors);
    const teamOverview = mapTeamOverview(teamsReport, categories, this.intervals, this.data.colors);

    this.pyramidCategoriesSubject.next(categories);
    this.scoreDevelopmentChipsSubject.next(
      mapScoreDevelopmentChips(categories, this.translate.instant('td_score_development_overall'), this.data.colors)
    );
    this.overallScoreSubject.next(
      mapOverallScore(workspaceReport, teamOverview.rows.length, this.intervals, this.data.colors)
    );
    this.teamOverviewSubject.next(teamOverview);
    this.scoreDevelopmentSubject.next(mapScoreDevelopment(scores));
  }

  private getScoreDevelopmentOptions(payload: TeamDynamicRequestDto) {
    return {
      startDate: payload.startDate,
      endDate: payload.endDate,
      timedQuestionCollectionUuid: payload.timedQuestionCollectionUuid ?? undefined
    };
  }

  private setPyramidLegend(): void {
    this.pyramidLegendSubject.next([
      {label: this.translate.instant('wo_team_dynamic_legend_strong'), color: this.data.colors.green},
      {label: this.translate.instant('wo_team_dynamic_legend_moderate'), color: this.data.colors.yellow},
      {label: this.translate.instant('wo_team_dynamic_legend_attention'), color: this.data.colors.orange},
      {label: this.translate.instant('wo_team_dynamic_legend_critical'), color: this.data.colors.red}
    ]);
  }
}
